import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GrayTypography } from './shoppingCartStyles';
import { DeliveryType, SalesPoint } from 'orderme-api-integration-client';
import { SalePointClient } from '../../helpers/client';
import { CompanyName } from '../../helpers/clientConfigs';
import { Typography, Stack } from '@mui/material';

export function DeliveryInfo() {
  const salePointClient = useMemo<SalePointClient>(() => new SalePointClient(), []);
  const deliveryType = useAppSelector((state: RootState) => state.pickupState.deliveryType);
  const orderDestination = useAppSelector((state: RootState) => state.pickupState.orderDestination);
  const parcelLocker = useAppSelector((state: RootState) => state.pickupState.parcelLocker);
  const totalPrice = useAppSelector((state: RootState) => state.shoppingCartState.totalPrice);
  const freeDeliverFrom = useAppSelector((state: RootState) => state.pickupState.freeDeliveryFrom);
  const [salesPoint, setSalePoint] = useState<SalesPoint>(null);
  const [priceToFreeDelivery, setPriceToFreeDelivery] = useState<number>(0);
  const { t } = useTranslation(['payment']);

  useEffect(() => {
    if (orderDestination?.knownId) {
      salePointClient
        .getById(orderDestination.knownId)
        .then((response) => {
          setSalePoint(response);
        })
        .catch((ex) => console.error(ex));

      return;
    } else {
      setSalePoint(null);
    }
  }, [orderDestination, salePointClient]);

  useEffect(() => {
    if (freeDeliverFrom > totalPrice) {
      setPriceToFreeDelivery(freeDeliverFrom - totalPrice);
    } else {
      setPriceToFreeDelivery(0);
    }
  }, [totalPrice, freeDeliverFrom]);

  const getDeliveryFullName = (salesPoint: SalesPoint) => {
    if (!salesPoint) {
      return '';
    }

    return `${salesPoint.name}, ${salesPoint.address} ${salesPoint.city}`;
  };

  const getDeliveryFromMessage = () => {
    const freeDeliveryFromPrice = freeDeliverFrom.toPrice();
    switch (deliveryType) {
      case DeliveryType.ForeignLocker:
        return t('payment:parcelLockerFreeDeliveryFrom', {
          price: freeDeliveryFromPrice,
        });
      case DeliveryType.Wardrobe:
        return t('payment:businessLockerFreeDeliveryFrom', {
          price: freeDeliveryFromPrice,
        });
      default:
        return t('payment:freeDeliveryFrom', { price: freeDeliveryFromPrice });
    }
  };

  return (
    <>
      {deliveryType === DeliveryType.SalesPoint &&
        (orderDestination.knownId ? (
          <>
            <GrayTypography>{t('payment:deliveryToSalesPoint', { companyName: CompanyName })}</GrayTypography>
            <GrayTypography>{getDeliveryFullName(salesPoint)}</GrayTypography>
          </>
        ) : (
          <GrayTypography>{t('payment:deliveryToAnySalesPoint', { companyName: CompanyName })}</GrayTypography>
        ))}
      {deliveryType === DeliveryType.Address && (
        <>
          <GrayTypography>{t('payment:deliveryFromHome')}</GrayTypography>
          <GrayTypography>{orderDestination.address.fullAddress}</GrayTypography>
          {orderDestination.address.apartmentNumber && (
            <GrayTypography>
              {t('payment:apartmentNo', { apartmentNo: orderDestination.address.apartmentNumber })}
            </GrayTypography>
          )}
        </>
      )}
      {deliveryType === DeliveryType.Wardrobe &&
        (orderDestination.knownId ? (
          <>
            <GrayTypography>{t('payment:deliveryToWardrobe')}</GrayTypography>
            <GrayTypography>{getDeliveryFullName(salesPoint)}</GrayTypography>
          </>
        ) : (
          <GrayTypography>{t('payment:deliveryToAnySalesPoint', { companyName: CompanyName })}</GrayTypography>
        ))}
      {deliveryType === DeliveryType.ForeignLocker && (
        <>
          <GrayTypography>
            {t('payment:deliveryToParcelLocker', { providerName: orderDestination.parcelLocker.provider })}
          </GrayTypography>
          <GrayTypography>{parcelLocker.value.name}</GrayTypography>
          <GrayTypography>{parcelLocker.value.address}</GrayTypography>
        </>
      )}
      {priceToFreeDelivery > 0 && (
        <Stack sx={{ color: 'error.main' }} direction="column">
          <Typography variant="body2"> {getDeliveryFromMessage()}</Typography>
          <Typography variant="body2">
            {t('payment:priceUpToFreeDelivery', {
              price: priceToFreeDelivery.toPrice(),
            })}
          </Typography>
        </Stack>
      )}
    </>
  );
}
